<script setup lang="ts">
import {
  Eye as EyeIcon,
  EyeOff as EyeOffIcon,
  Download as DownloadIcon,
  MoreHorizontal as MoreHorizontalIcon,
  Route as RouteIcon,
  Activity as ActivityIcon,
  Bike as BikeIcon,
  Car as CarIcon,
  Train as TrainIcon,
  Footprints as FootprintsIcon,
  Waves as WavesIcon,
  Sailboat as SailboatIcon,
  Dumbbell as DumbbellIcon,
  Plane as PlaneIcon,
  Copy as CopyIcon,
  SquarePlus as SquarePlusIcon,
  SquareMinus as SquareMinusIcon,
  Tag as TagIcon,
  PenLine as PenLineIcon,
  Pin as PinIcon,
  PinOff as PinOffIcon,
  Trash2 as Trash2Icon,
  X as XIcon,
} from '@lucide/vue'
import { computed, ref, onBeforeUnmount, watch, nextTick, type ComponentPublicInstance } from 'vue'
import { useRoute } from 'vue-router'

import GpxDownloadOptionsModal from '@/components/map/modals/GpxDownloadOptionsModal.vue'
import BaseCheckbox from '@/components/ui/BaseCheckbox.vue'
import InlineEditField from '@/components/ui/InlineEditField.vue'
import MobileActionSheet from '@/components/ui/MobileActionSheet.vue'
import { useEventListener } from '@/composables/useEventListener'
import { useSwipeAction } from '@/composables/useSwipeAction'
import {
  openAddToCollection as openAddToCollectionPicker,
  tourToAddToCollectionItem,
} from '@/services/addToCollectionService'
import { apiService } from '@/services/apiService'
import { showConfirm } from '@/services/dialogService'
import { downloadFile } from '@/services/downloadService'
import { formatTextWithNewlines } from '@/services/formattingService'
import { logService, LOG_CATEGORIES } from '@/services/logService'
import { MOBILE_PANEL_DRAG_START_EVENT } from '@/services/mobilePanelGestureService'
import { openMobileRenameSheet } from '@/services/mobileRenameService'
import { hasPermission, requirePermission } from '@/services/permissionService'
import { toggleTourPinned } from '@/services/pinnedService'
import { showError } from '@/services/toastService'
import { getTrackTypeCategory, shouldHideAscentForTour } from '@/services/tourTypeService'
import {
  calculateTrackDistance,
  getTrackElevationMax,
  getTrackElevationMin,
  getTrackElevationGain,
  getTrackElevationLoss,
  getTrackTimeElapsed,
  getTrackTimeMoving,
} from '@/services/trackMetricsCalculationService'
import { isSmallScreen } from '@/services/uiService'
import { useAuthStore } from '@/stores/authStore'
import { useLibraryStore } from '@/stores/libraryStore'
import { useUiStore } from '@/stores/uiStore'
import { useWorkspaceStore } from '@/stores/workspaceStore'
import type { Tour } from '@/types/tour'
import { getCreatorByline } from '@/utils/creatorByline'
import { requireItemEditable } from '@/utils/permissionDialog'
import { resolveTourAccentColor } from '@/utils/tourAccentColor'
import {
  getEffectiveTourName,
  getTourGlobalNameSubtitle,
  getWorkspaceTourDownloadName,
  hasCollectionNameOverride,
} from '@/utils/tourName'
import { getRootSourceTour, getRootSourceTourClientId } from '@/utils/tourSource'

import TourDeleteModal from '../modals/TourDeleteModal.vue'

import WorkspaceTourActionSheetContent from './WorkspaceTourActionSheetContent.vue'

const workspaceStore = useWorkspaceStore()
const uiStore = useUiStore()
const authStore = useAuthStore()
const libraryStore = useLibraryStore()
const route = useRoute()

const props = defineProps<{
  /** When provided, overrides the store's tour list with a filtered/sorted subset. Drag reordering is disabled. */
  overrideTours?: Tour[]
  selectionMode?: boolean
  selectedTourIds?: Set<string>
}>()

const isDragDisabled = computed(() => props.overrideTours !== undefined)

// A tour is an activity if it has a sport type or any track with a recorded start time
const isTourActivity = (tour: Tour): boolean =>
  !!tour.type || tour.tracks.some((t) => !!t.startTime)

// Creator-only edits remain visible and explanatory. Whole-tour Delete is the documented hidden
// exception because collection-role changes cannot grant creator ownership.
const canEditTourContent = (_tour: Tour): boolean => hasPermission('edit_track')
const canDeleteTourGlobally = (tour: Tour): boolean =>
  hasPermission('delete_tour') && tour.canEdit !== false
const isPinnedWorkspace = computed(
  () =>
    route.name === 'pinned' ||
    (workspaceStore.isSystem === true && workspaceStore.systemKey === 'pinned'),
)
const canRemoveTourFromWorkspace = computed(
  () => hasPermission('delete_tour') && !isPinnedWorkspace.value,
)

const getTourTypeIcon = (tour: Tour) => {
  if (!tour.type) {
    return isTourActivity(tour) ? ActivityIcon : RouteIcon
  }
  const category = getTrackTypeCategory(tour.type)
  switch (category) {
    case 'aviation':
      return PlaneIcon
    case 'cycling':
      return BikeIcon
    case 'driving':
      return CarIcon
    case 'rail':
      return TrainIcon
    case 'hiking':
    case 'running':
    case 'walking':
      return FootprintsIcon
    case 'swimming':
    case 'diving':
      return WavesIcon
    case 'water_sports':
      return SailboatIcon
    case 'gym_fitness_equipment':
      return DumbbellIcon
    default:
      return ActivityIcon
  }
}

const emit = defineEmits<{
  'zoom-tour': [tourId: string]
  'select-tour': [tourId: string]
  'collapse-panels': []
  'edit-tour': [tourId: string]
  'enter-track-edit': [tourId: string, trackClientId: string]
  'navigate-to-workspace': [workspaceId: string, workspaceName: string]
  'toggle-selection': [tourId: string]
}>()

const tours = computed(() => props.overrideTours ?? workspaceStore.allTours)

/**
 * Computes display names for tours by removing workspace prefix
 * Examples:
 * Workspace: "ARA München"
 * - "ARA München - T1" -> "T1"
 * - "ARA München - T2 Foo" -> "T2 Foo"
 * - "ARA München foo" -> "foo"
 * - "ARA München" -> "ARA München"
 * - "Foobar" -> "Foobar"
 * - "" -> ""
 */
const getDisplayName = (tourName: string): string => {
  const workspaceName = workspaceStore.name
  if (!workspaceName || !tourName || tourName === workspaceName) {
    return tourName
  }

  // Remove workspace name prefix with separator (dash or space)
  const patterns = [
    `${workspaceName} - `, // "ARA München - T1"       "T1"
    `${workspaceName}-`, // "ARA München-01-foo"       "foo"
    `${workspaceName} `, // "ARA München foo"          "foo"
    `${workspaceName} > `, // "ARA München > T1"       "T1"
    `${workspaceName} / `, // "ARA München / foo"      "foo"
    `${workspaceName} -> `, // "ARA München -> foo"    "foo"
    `${workspaceName} => `, // "ARA München => foo"    "foo"
  ]

  for (const pattern of patterns) {
    if (tourName.startsWith(pattern)) {
      return tourName.substring(pattern.length)
    }
  }

  return tourName
}

const getWorkspaceDisplayName = (tour: Tour): string => getDisplayName(getEffectiveTourName(tour))
const getTourCreatorByline = (tour: Tour): string | null => getCreatorByline(tour)

const expandedTourIds = ref<Set<string>>(new Set())
const downloadingTourIds = ref<Set<string>>(new Set())
const contextMenuTourId = ref<string | null>(null)
const contextMenuStyle = ref<Record<string, string>>({})
const mobileContextMenuOpen = ref(false)
const longPressTourId = ref<string | null>(null)
const longPressActivated = ref(false)
let longPressTimer: ReturnType<typeof setTimeout> | null = null
const downloadTourTarget = ref<Tour | null>(null)
const isGpxDownloadModalOpen = ref(false)
const isGpxDownloadModalDownloading = computed(
  () =>
    !!downloadTourTarget.value && downloadingTourIds.value.has(downloadTourTarget.value.clientId),
)
const deleteTargetTour = ref<Tour | null>(null)
const isDeleteTourModalOpen = computed(() => deleteTargetTour.value !== null)
const isDeletingTour = ref(false)
const draggedTourId = ref<string | null>(null)
const dragOverIndex = ref<number | null>(null)
const touchDragActive = ref(false)
const touchStartY = ref(0)
const touchCurrentY = ref(0)
const touchDraggedTourId = ref<string | null>(null)

const autoScrollTimer = ref<number | null>(null)
const SCROLL_SPEED = 4
const SCROLL_ZONE = 100

const tourListContainer = ref<HTMLElement | null>(null)
const tourElementRefs = ref<Map<string, HTMLElement>>(new Map())
const SECTION_HEADER_HEIGHT = 38

const findNearestScrollableAncestor = (element: HTMLElement): HTMLElement | null => {
  let current = element.parentElement

  while (current) {
    const style = window.getComputedStyle(current)
    const overflowY = style.overflowY
    const isScrollable =
      (overflowY === 'auto' || overflowY === 'scroll') &&
      current.scrollHeight > current.clientHeight
    if (isScrollable) {
      return current
    }
    current = current.parentElement
  }

  return null
}

const getToursTopHintOffset = () => {
  const container = tourListContainer.value
  if (!container) return 0

  const sidebarPanel = container.closest('[data-testid="workspace-sidebar-panel"]')
  if (!(sidebarPanel instanceof HTMLElement)) return 0

  const toursTopHint = sidebarPanel.querySelector('[data-testid="tours-top-hint"]')
  if (!(toursTopHint instanceof HTMLElement)) return 0

  return toursTopHint.getBoundingClientRect().height || SECTION_HEADER_HEIGHT
}

const scrollTourRowIntoView = (row: HTMLElement) => {
  const container = findNearestScrollableAncestor(row)
  if (!container) {
    row.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
    return
  }

  const containerRect = container.getBoundingClientRect()
  const rowRect = row.getBoundingClientRect()
  const topOffset = getToursTopHintOffset()
  const visibleTop = containerRect.top + topOffset
  const visibleBottom = containerRect.bottom

  if (rowRect.top < visibleTop) {
    const targetTop = container.scrollTop + (rowRect.top - containerRect.top) - topOffset
    container.scrollTo({ top: Math.max(0, targetTop), behavior: 'smooth' })
    return
  }

  if (rowRect.bottom > visibleBottom) {
    const targetTop = container.scrollTop + (rowRect.bottom - containerRect.bottom)
    container.scrollTo({ top: Math.max(0, targetTop), behavior: 'smooth' })
  }
}

const handleAutoScroll = (container: HTMLElement, scrollTop: boolean) => {
  if (!container || !container.isConnected || !tourListContainer.value) {
    stopAutoScroll()
    return
  }

  if (scrollTop) {
    container.scrollTop -= SCROLL_SPEED
  } else {
    container.scrollTop += SCROLL_SPEED
  }
}

const startAutoScroll = (scrollTop: boolean) => {
  stopAutoScroll()

  if (!tourListContainer.value || !tourListContainer.value.isConnected) return

  autoScrollTimer.value = window.setInterval(() => {
    if (!tourListContainer.value || !tourListContainer.value.isConnected) {
      stopAutoScroll()
      return
    }
    handleAutoScroll(tourListContainer.value, scrollTop)
  }, 16) // ~60fps
}

const stopAutoScroll = () => {
  if (autoScrollTimer.value) {
    clearInterval(autoScrollTimer.value)
    autoScrollTimer.value = null
  }
}

const checkAutoScroll = (clientY: number) => {
  if (!tourListContainer.value || !tourListContainer.value.isConnected) {
    stopAutoScroll()
    return
  }

  const rect = tourListContainer.value.getBoundingClientRect()
  const distanceFromTop = clientY - rect.top
  const distanceFromBottom = rect.bottom - clientY

  if (distanceFromTop < SCROLL_ZONE && tourListContainer.value.scrollTop > 0) {
    startAutoScroll(true)
  } else if (
    distanceFromBottom < SCROLL_ZONE &&
    tourListContainer.value.scrollTop <
      tourListContainer.value.scrollHeight - tourListContainer.value.clientHeight
  ) {
    startAutoScroll(false)
  } else {
    stopAutoScroll()
  }
}

const isTourExpanded = (tourId: string): boolean => {
  return expandedTourIds.value.has(tourId)
}

const updateFloatingPanelPosition = (
  event: MouseEvent,
  width: number,
  stylesRef: typeof contextMenuStyle,
) => {
  const viewportPadding = 8
  const left = Math.min(event.clientX, window.innerWidth - width - viewportPadding)
  const top = Math.min(event.clientY, window.innerHeight - 16 - viewportPadding)
  stylesRef.value = {
    position: 'fixed',
    left: `${Math.max(viewportPadding, left)}px`,
    top: `${Math.max(viewportPadding, top)}px`,
  }
}

const closeMobileContextMenu = () => {
  if (!mobileContextMenuOpen.value) return
  mobileContextMenuOpen.value = false
}

const clearContextMenuTourHighlight = () => {
  if (contextMenuTourId.value && workspaceStore.hoveredTourId === contextMenuTourId.value) {
    workspaceStore.setHoveredTourId(null)
  }
}

const handleMobileContextMenuClosed = () => {
  clearContextMenuTourHighlight()
  contextMenuTourId.value = null
  longPressTourId.value = null
  longPressActivated.value = false
}

const closeContextMenu = () => {
  if (mobileContextMenuOpen.value) {
    closeMobileContextMenu()
    return
  }
  clearContextMenuTourHighlight()
  contextMenuTourId.value = null
}

const openTourContextMenu = (tour: Tour, event: MouseEvent) => {
  event.preventDefault()
  event.stopPropagation()
  if (isCoarsePointer()) return
  contextMenuTourId.value = tour.clientId
  workspaceStore.setHoveredTourId(tour.clientId)
  updateFloatingPanelPosition(event, 192, contextMenuStyle)
}

const contextMenuTour = computed(() =>
  contextMenuTourId.value ? workspaceStore.getTourById(contextMenuTourId.value) : null,
)

const isCoarsePointer = () =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(pointer: coarse)').matches

const clearLongPressTimer = () => {
  if (longPressTimer !== null) {
    clearTimeout(longPressTimer)
    longPressTimer = null
  }
}

const startTourLongPress = (event: TouchEvent, tour: Tour) => {
  if (renamingTourId.value === tour.clientId) return
  if (event.touches.length !== 1) return
  const target = event.target instanceof HTMLElement ? event.target : null
  if (target?.closest('button,input,textarea,[contenteditable="true"]')) return

  clearLongPressTimer()
  longPressActivated.value = false
  longPressTourId.value = tour.clientId

  longPressTimer = setTimeout(() => {
    longPressTimer = null
    longPressActivated.value = true
    longPressTourId.value = tour.clientId
    try {
      navigator.vibrate?.(30)
    } catch (error) {
      logService.debug(LOG_CATEGORIES.UI, 'Long-press haptic feedback unavailable', { error })
    }
    emit('zoom-tour', tour.clientId)
    contextMenuTourId.value = tour.clientId
    workspaceStore.setHoveredTourId(tour.clientId)
    contextMenuStyle.value = {
      position: 'fixed',
      left: '16px',
      right: '16px',
      bottom: '80px',
    }
    mobileContextMenuOpen.value = true
  }, 500)
}

const endTourLongPress = () => {
  clearLongPressTimer()
  if (!longPressActivated.value) {
    longPressTourId.value = null
  }
}

const cancelTourRowLongPress = () => {
  clearLongPressTimer()
  longPressTourId.value = null
}

const cancelTourContextMenuForPanelDrag = () => {
  cancelTourRowLongPress()
  longPressActivated.value = false
  if (mobileContextMenuOpen.value) closeMobileContextMenu()
}

const tourSwipe = useSwipeAction<Tour>({
  getId: (tour) => tour.clientId,
  onCancelCompetingGesture: cancelTourRowLongPress,
  onTrigger: (tour) => handleTogglePinned(tour),
})

const onTourRowTouchStart = (event: TouchEvent, tour: Tour) => {
  tourSwipe.start(event, tour)
  startTourLongPress(event, tour)
}

const onTourRowTouchEnd = (tour: Tour) => {
  tourSwipe.end(tour)
  endTourLongPress()
}

const onTourRowTouchCancel = (tour: Tour) => {
  tourSwipe.cancel(tour)
  endTourLongPress()
}

const getTourDragHandleColor = (tour: Tour): string => resolveTourAccentColor(tour)

const tourStats = computed(() => {
  return tours.value.map((tour: Tour) => {
    const totalDistance = calculateTourDistance(tour)

    let elevation_min: number | null = null
    let elevation_max: number | null = null
    let elevation_gain: number | null = null
    let elevation_loss: number | null = null

    // Prefer server-cached tour-level fields; fall back to summing from tracks
    let time_moving: number | null =
      tour.time_moving !== null && tour.time_moving !== undefined ? tour.time_moving : null
    let time_elapsed: number | null =
      tour.time_elapsed !== null && tour.time_elapsed !== undefined ? tour.time_elapsed : null

    tour.tracks.forEach((track) => {
      const trackMin = getTrackElevationMin(track)
      if (trackMin !== null) {
        elevation_min = elevation_min === null ? trackMin : Math.min(elevation_min, trackMin)
      }

      const trackMax = getTrackElevationMax(track)
      if (trackMax !== null) {
        elevation_max = elevation_max === null ? trackMax : Math.max(elevation_max, trackMax)
      }

      if (time_moving === null) {
        const trackTimeMoving = getTrackTimeMoving(track)
        if (trackTimeMoving !== null) {
          time_moving = (time_moving || 0) + trackTimeMoving
        }
      }

      if (time_elapsed === null) {
        const trackTimeElapsed = getTrackTimeElapsed(track)
        if (trackTimeElapsed !== null) {
          time_elapsed = (time_elapsed || 0) + trackTimeElapsed
        }
      }

      // Elevation gain/loss: prefer track.metrics, fall back to summing BRouter
      // filtered_ascend/filtered_descend across segments (drawn tours).
      if (!shouldHideAscentForTour(tour)) {
        const trackGain = getTrackElevationGain(track)
        if (trackGain !== null) {
          elevation_gain = (elevation_gain || 0) + trackGain
        }
      }
      const trackLoss = getTrackElevationLoss(track)
      if (trackLoss !== null) {
        elevation_loss = (elevation_loss || 0) + trackLoss
      }
    })

    let averageSpeed: number | null = null
    if (time_moving && time_moving > 0 && totalDistance > 0) {
      averageSpeed = totalDistance / 1000 / (time_moving / 3600)
    }

    return {
      clientId: tour.clientId,
      distance: totalDistance,
      elevation: {
        min: elevation_min !== null ? Math.round(elevation_min) : null,
        max: elevation_max !== null ? Math.round(elevation_max) : null,
        gain: elevation_gain !== null ? Math.round(elevation_gain) : null,
        loss: elevation_loss !== null ? Math.round(elevation_loss) : null,
      },
      time: {
        moving: time_moving,
        elapsed: time_elapsed,
      },
      averageSpeed: averageSpeed,
    }
  })
})

const isTourVisible = (tour: Tour): boolean => tour.visible !== false

const toggleTourVisibility = (tourId: string, currentVisibility: boolean | undefined) => {
  workspaceStore.setTourVisibility(tourId, !(currentVisibility ?? true))
}

const removeTourFromWorkspace = async (tourId: string) => {
  if (!(await requirePermission('delete_tour'))) return
  const tour = workspaceStore.getTourById(tourId)
  if (!tour) {
    return
  }
  // Unlinking is owner-or-creator; check before the "are you sure" prompt so a plain
  // collaborator sees only the explainer, not a confirm they can't actually act on.
  if (
    !(await requireItemEditable(
      workspaceStore.isOwner ? true : tour.canEdit,
      'activity',
      tour.createdByName,
    ))
  )
    return

  const confirmed = await showConfirm({
    title: 'Remove Tour',
    message: `Are you sure you want to remove "${getEffectiveTourName(tour)}" from this workspace? It will still be available in your other workspaces.`,
    confirmText: 'Remove',
    cancelText: 'Cancel',
    variant: 'danger',
  })

  if (confirmed) {
    workspaceStore.clearTour(tourId)
    if (workspaceStore._selectedTourId === tourId) {
      workspaceStore.setSelectedTourId(null)
    }
  }
}

const openDeleteTourModal = async (tourId: string) => {
  const tour = workspaceStore.getTourById(tourId)
  if (!tour || !canDeleteTourGlobally(tour)) return
  if (!(await requirePermission('delete_tour'))) return
  // Check creator-only content edit *before* opening the "are you sure" modal — otherwise the
  // explainer dialog fires from inside that modal's confirm handler and, since dialogService
  // only supports one dialog at a time, it never becomes visible and the confirm button hangs
  // forever on "Deleting…".
  if (!(await requireItemEditable(tour.canEdit, 'activity', tour.createdByName))) return
  deleteTargetTour.value = tour
}

const closeDeleteTourModal = () => {
  if (isDeletingTour.value) return
  deleteTargetTour.value = null
}

const confirmDeleteTourGlobally = async () => {
  const tour = deleteTargetTour.value
  if (!tour) return

  isDeletingTour.value = true
  try {
    await libraryStore.deleteTour(tour.clientId)
    const tourIndex = workspaceStore.tours.findIndex(
      (candidate) => candidate.clientId === tour.clientId,
    )
    if (tourIndex !== -1) {
      workspaceStore.tours.splice(tourIndex, 1)
    }
    if (workspaceStore._selectedTourId === tour.clientId) {
      workspaceStore.setSelectedTourId(null)
    }
    deleteTargetTour.value = null
  } catch (error) {
    logService.error(LOG_CATEGORIES.API, 'Failed to delete tour from TourList', {
      error,
      tourId: tour.clientId,
    })
    showError(`Failed to delete tour: ${error instanceof Error ? error.message : String(error)}`)
  } finally {
    isDeletingTour.value = false
  }
}

const renamingTourId = ref<string | null>(null)
const renameValue = ref('')
const renameInputRef = ref<{ inputEl: HTMLInputElement | null } | null>(null)
const renamePlaceholder = computed(() => {
  const tourId = renamingTourId.value
  if (!tourId) return ''
  const tour = workspaceStore.getTourById(tourId)
  if (!tour) return ''
  return tour.name ?? ''
})
const getTourGlobalName = (tour: Tour): string | null => getTourGlobalNameSubtitle(tour)

const startInlineRename = async (tour: Tour) => {
  if (!canEditTourContent(tour)) return
  if (!(await requirePermission('edit_track'))) return
  closeContextMenu()

  if (isSmallScreen.value) {
    const tourId = tour.clientId
    openMobileRenameSheet({
      title: `Rename ${isTourActivity(tour) ? 'activity' : 'route'}`,
      currentName: getWorkspaceDisplayName(tour),
      submit: async (name) => {
        const currentTour = workspaceStore.getTourById(tourId)
        if (currentTour && name === getWorkspaceDisplayName(currentTour)) return
        await workspaceStore.updateTourCollectionName(tourId, name)
      },
    })
    return
  }

  renameValue.value = getWorkspaceDisplayName(tour)
  renamingTourId.value = tour.clientId
  nextTick(() => {
    renameInputRef.value?.inputEl?.focus()
    renameInputRef.value?.inputEl?.select()
  })
}

const commitInlineRename = async () => {
  if (!renamingTourId.value) return
  const tourId = renamingTourId.value
  const trimmed = renameValue.value.trim()
  renamingTourId.value = null
  const tour = workspaceStore.getTourById(tourId)
  if (!trimmed) {
    await workspaceStore.updateTourCollectionName(tourId, null)
    return
  }
  if (tour && trimmed === getWorkspaceDisplayName(tour)) return
  await workspaceStore.updateTourCollectionName(tourId, trimmed)
}

const cancelInlineRename = () => {
  renamingTourId.value = null
  renameValue.value = ''
}

// ── Add to collection ─────────────────────────────────────────────────────────

const openAddToCollection = (tour: Tour) => {
  closeContextMenu()
  if (!authStore.isAuthenticated) {
    window.dispatchEvent(new CustomEvent('open-auth-modal', { detail: { view: 'login' } }))
    return
  }
  openAddToCollectionPicker({ tours: [tourToAddToCollectionItem(tour)] })
}

const duplicateTour = async (tour: Tour, redrawAfter = false) => {
  closeContextMenu()
  try {
    const hydratedTour =
      (await workspaceStore.ensureTourDetailLoaded?.(tour.clientId)) ??
      workspaceStore.getTourById(tour.clientId) ??
      tour
    const isActivity = isTourActivity(hydratedTour)
    const newTourId = await workspaceStore.addEmptyTour({
      name: `${hydratedTour.name} (copy)`,
      type: isActivity ? undefined : (hydratedTour.type ?? undefined),
      sourceTourClientId: getRootSourceTourClientId(hydratedTour),
      sourceTour: getRootSourceTour(hydratedTour),
      emitAddedEvent: false,
    })
    const duplicatedTrackIds = new Map<string, string>()
    for (const track of hydratedTour.tracks) {
      const newTrackId = await workspaceStore.addTrack(newTourId, {
        name: track.name ?? '',
        segments: track.segments.map(({ clientId: _cid, ...seg }) => ({
          ...seg,
          points: seg.points.map((p) => ({ ...p })),
        })),
        color: track.color,
        routingProfile: isActivity ? undefined : track.routingProfile,
      })
      if (newTrackId) {
        duplicatedTrackIds.set(track.clientId, newTrackId)
      }
    }

    for (const routePoint of hydratedTour.routePoints) {
      const newTrackClientId = duplicatedTrackIds.get(routePoint.trackClientId)
      if (!newTrackClientId) continue

      await workspaceStore.addRoutePoint(newTourId, {
        lat: routePoint.lat,
        lon: routePoint.lon,
        ele: routePoint.ele,
        name: routePoint.name,
        description: routePoint.description,
        order: routePoint.order,
        trackClientId: newTrackClientId,
      })
    }

    if (redrawAfter) {
      workspaceStore.setSelectedTourId(newTourId)
      emit('select-tour', newTourId)
      const firstOriginalTrackId = hydratedTour.tracks[0]?.clientId
      const newFirstTrackId = firstOriginalTrackId
        ? duplicatedTrackIds.get(firstOriginalTrackId)
        : undefined
      if (newFirstTrackId) {
        emit('enter-track-edit', newTourId, newFirstTrackId)
      } else {
        window.dispatchEvent(
          new CustomEvent('start-track-drawing', { detail: { tourId: newTourId } }),
        )
      }
    }
  } catch (err) {
    logService.error(LOG_CATEGORIES.GENERAL, 'Failed to duplicate tour', { error: err })
    showError('Failed to duplicate tour')
  }
}

const formatDistance = (meters: number): string => {
  if (meters >= 1000) return (meters / 1000).toFixed(1) + ' km'
  return `${Math.round(meters)} m`
}

const formatTourDate = (tour: Tour): string | null => {
  const startTime = tour.tracks.find((t) => !!t.startTime)?.startTime
  const dateStr = startTime ? new Date(startTime).toISOString() : (tour.createdAt ?? null)
  if (!dateStr) return null
  const d = new Date(dateStr)
  const isCurrentYear = d.getFullYear() === new Date().getFullYear()
  if (isCurrentYear) return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
  const base = d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
  return base.replace(String(d.getFullYear()), `'${String(d.getFullYear()).slice(2)}`)
}

const getTourMetaParts = (tour: Tour): string[] => {
  const parts: string[] = []
  const date = formatTourDate(tour)
  if (date) parts.push(date)
  const stats = tourStats.value.find((s) => s.clientId === tour.clientId)
  if (stats) {
    if (stats.distance > 0) parts.push(formatDistance(stats.distance))
    if (stats.elevation.gain && stats.elevation.gain > 0) parts.push(`+${stats.elevation.gain} m`)
  }
  return parts
}

function calculateTourDistance(tour: Tour): number {
  return tour.tracks.reduce((sum, track) => sum + calculateTrackDistance(track), 0)
}

const handleRouteAction = async (tour: Tour) => {
  if (!canEditTourContent(tour)) return
  if (!(await requirePermission('edit_track'))) return

  closeContextMenu()

  if (tour.tracks.length === 0) {
    window.dispatchEvent(
      new CustomEvent('start-track-drawing', { detail: { tourId: tour.clientId } }),
    )
    return
  }

  if (tour.tracks.length === 1) {
    const trackClientId = tour.tracks[0]?.clientId
    if (!trackClientId) return
    emit('enter-track-edit', tour.clientId, trackClientId)
    return
  }

  emit('select-tour', tour.clientId)

  await showConfirm({
    title: 'Select a track in Tracks',
    message:
      'This tour has multiple tracks. Open the tour details and start route editing directly from the Tracks panel for the track you want to edit.',
    confirmText: 'OK',
    cancelText: undefined,
    variant: 'info',
  })
}

const downloadGpx = async (tour: Tour, includeRoutePoints: boolean) => {
  try {
    downloadingTourIds.value.add(tour.clientId)

    // Add a minimum loading time to ensure spinner is visible
    const [blob] = await Promise.all([
      apiService.downloadGpx(tour.clientId, { includeRoutePoints }),
      new Promise((resolve) => setTimeout(resolve, 500)), // Minimum 500ms loading
    ])

    const downloadName = getWorkspaceTourDownloadName(tour)
    const fileName = `${downloadName}.gpx`
    await downloadFile(blob, fileName, {
      mimeType: 'application/gpx+xml',
    })
  } finally {
    downloadingTourIds.value.delete(tour.clientId)
  }
}

const closeGpxDownloadModal = () => {
  isGpxDownloadModalOpen.value = false
  downloadTourTarget.value = null
}

const openGpxDownloadModal = (tour: Tour, event?: Event) => {
  event?.stopPropagation()
  if (downloadingTourIds.value.has(tour.clientId)) {
    return
  }

  closeContextMenu()
  downloadTourTarget.value = tour
  isGpxDownloadModalOpen.value = true
}

const handleContextMenuRename = async () => {
  if (!contextMenuTourId.value) return
  const tour = workspaceStore.getTourById(contextMenuTourId.value)
  if (!tour) return
  closeContextMenu()
  await startInlineRename(tour)
}

const handleContextMenuDownload = () => {
  if (!contextMenuTourId.value) return
  const tour = workspaceStore.getTourById(contextMenuTourId.value)
  if (!tour) return
  closeContextMenu()
  openGpxDownloadModal(tour)
}

const handleContextMenuAddToCollection = () => {
  if (!contextMenuTourId.value) return
  const tour = workspaceStore.getTourById(contextMenuTourId.value)
  if (!tour) return
  closeContextMenu()
  openAddToCollection(tour)
}

const handleContextMenuToggleVisibility = () => {
  if (!contextMenuTourId.value) return
  const tour = workspaceStore.getTourById(contextMenuTourId.value)
  if (!tour) return
  closeContextMenu()
  toggleTourVisibility(tour.clientId, tour.visible)
}

const handleTogglePinned = async (tour: Tour) => {
  closeContextMenu()
  if (!authStore.isAuthenticated) {
    window.dispatchEvent(new CustomEvent('open-auth-modal', { detail: { view: 'login' } }))
    return
  }
  await toggleTourPinned(tour)
}

const handleContextMenuTogglePinned = async () => {
  if (!contextMenuTourId.value) return
  const tour = workspaceStore.getTourById(contextMenuTourId.value)
  if (!tour) return
  await handleTogglePinned(tour)
}

const handleContextMenuEditRoute = async () => {
  if (!contextMenuTourId.value) return
  const tour = workspaceStore.getTourById(contextMenuTourId.value)
  if (!tour) return
  closeContextMenu()
  await handleRouteAction(tour)
}

const handleContextMenuDuplicateOrEditAsRoute = async () => {
  if (!contextMenuTourId.value) return
  const tour = workspaceStore.getTourById(contextMenuTourId.value)
  if (!tour) return
  closeContextMenu()
  await duplicateTour(tour, isTourActivity(tour))
}

const handleContextMenuRemoveFromWorkspace = async () => {
  if (!contextMenuTourId.value) return
  const tourId = contextMenuTourId.value
  closeContextMenu()
  await removeTourFromWorkspace(tourId)
}

const handleContextMenuDeleteTourGlobally = async () => {
  if (!contextMenuTourId.value) return
  const tourId = contextMenuTourId.value
  closeContextMenu()
  await openDeleteTourModal(tourId)
}

const handleDownloadGpx = async (options: { includeRoutePoints: boolean }) => {
  const tour = downloadTourTarget.value
  if (!tour) {
    return
  }

  await downloadGpx(tour, options.includeRoutePoints)
  closeGpxDownloadModal()
}

const onDragStart = (event: DragEvent, tourId: string) => {
  if (!event.dataTransfer) return

  draggedTourId.value = tourId
  event.dataTransfer.effectAllowed = 'move'
  event.dataTransfer.setData('text/plain', tourId)
  event.dataTransfer.setData('application/x-tour-reorder', tourId)

  // Set a custom drag image to improve visual feedback
  const target = event.target as HTMLElement
  const rect = target.getBoundingClientRect()
  event.dataTransfer.setDragImage(target, rect.width / 2, rect.height / 2)

  // Stop propagation to prevent map from handling this drag
  event.stopPropagation()
}

const onDragEnd = () => {
  draggedTourId.value = null
  dragOverIndex.value = null
  stopAutoScroll()
}

const onDragOver = (event: DragEvent, index: number) => {
  event.preventDefault()
  event.stopPropagation()
  event.stopImmediatePropagation()
  if (!event.dataTransfer) return

  if (!event.dataTransfer.types.includes('application/x-tour-reorder')) {
    return false
  }

  event.dataTransfer.dropEffect = 'move'
  dragOverIndex.value = index

  checkAutoScroll(event.clientY)
}

const onDragLeave = (event: DragEvent) => {
  event.stopPropagation()
  event.stopImmediatePropagation()

  const target = event.target as HTMLElement
  const rect = target.getBoundingClientRect()
  const x = event.clientX
  const y = event.clientY

  if (x < rect.left || x > rect.right || y < rect.top || y > rect.bottom) {
    dragOverIndex.value = null
    stopAutoScroll()
  }
}

const onDrop = async (event: DragEvent, dropIndex: number) => {
  event.preventDefault()
  event.stopPropagation()
  event.stopImmediatePropagation()

  dragOverIndex.value = null
  stopAutoScroll()

  if (!event.dataTransfer?.types.includes('application/x-tour-reorder')) {
    return false
  }

  const draggedId = draggedTourId.value
  if (!draggedId) return

  const sortedTours = tours.value
  const draggedIndex = sortedTours.findIndex((tour) => tour.clientId === draggedId)

  if (draggedIndex !== -1 && draggedIndex !== dropIndex) {
    await workspaceStore.reorderTours(draggedIndex, dropIndex)
  }

  draggedTourId.value = null
}

const onTouchStart = (event: TouchEvent, tourId: string) => {
  touchDragActive.value = true
  touchDraggedTourId.value = tourId
  touchStartY.value = event.touches[0].clientY
  touchCurrentY.value = event.touches[0].clientY

  // Prevent scrolling while dragging and stop propagation
  event.preventDefault()
  event.stopPropagation()
}

const onTouchMove = (event: TouchEvent) => {
  if (!touchDragActive.value || !touchDraggedTourId.value || !tourListContainer.value) return

  touchCurrentY.value = event.touches[0].clientY

  checkAutoScroll(event.touches[0].clientY)

  if (!tourListContainer.value || !tourListContainer.value.isConnected) {
    stopAutoScroll()
    return
  }

  let newDragOverIndex: number | null = null
  const touchY = event.touches[0].clientY

  tours.value.forEach((tour, index) => {
    const element = tourElementRefs.value.get(tour.clientId)
    if (element) {
      const rect = element.getBoundingClientRect()
      if (touchY >= rect.top && touchY <= rect.bottom) {
        newDragOverIndex = index
      }
    }
  })

  dragOverIndex.value = newDragOverIndex

  event.preventDefault()
  event.stopPropagation()
}

const onTouchEnd = async () => {
  if (!touchDragActive.value || !touchDraggedTourId.value) return

  stopAutoScroll()

  const draggedId = touchDraggedTourId.value
  const dropIndex = dragOverIndex.value

  if (draggedId && dropIndex !== null) {
    const sortedTours = tours.value
    const draggedIndex = sortedTours.findIndex((tour) => tour.clientId === draggedId)

    if (draggedIndex !== -1 && draggedIndex !== dropIndex) {
      await workspaceStore.reorderTours(draggedIndex, dropIndex)
    }
  }

  touchDragActive.value = false
  touchDraggedTourId.value = null
  touchStartY.value = 0
  touchCurrentY.value = 0
  dragOverIndex.value = null
}

const closeAllToolbars = (event: Event) => {
  const target = event.target as HTMLElement
  if (!target.closest('[data-tour-context-menu]')) {
    closeContextMenu()
  }
}

const handleTourClick = async (tourId: string) => {
  if (renamingTourId.value) return
  if (tourSwipe.consumeSuppressedClick()) return
  if (longPressActivated.value) {
    longPressActivated.value = false
    return
  }
  if (!tourListContainer.value || !tourListContainer.value.isConnected) return

  stopAutoScroll()

  // Toggle: clicking an already-selected tour closes the detail panel and deselects it
  if (workspaceStore.selectedTour?.clientId === tourId && uiStore.activePanel === 'tourDetail') {
    workspaceStore.setSelectedTourId(null)
    uiStore.hideTourDetailPanel()
    return
  }

  // Open detail view
  emit('zoom-tour', tourId)
  workspaceStore.setSelectedTourId(tourId)
  emit('select-tour', tourId)

  if (uiStore.activePanel !== 'tourDetail') {
    uiStore.showTourDetailPanel()
  }
}

const setTourElementRef = (el: Element | ComponentPublicInstance | null, tourId: string) => {
  if (el instanceof HTMLElement) {
    tourElementRefs.value.set(tourId, el)
  } else {
    tourElementRefs.value.delete(tourId)
  }
}

useEventListener(document, 'touchmove', onTouchMove, { passive: false })
useEventListener(document, 'touchend', onTouchEnd)
useEventListener(document, 'click', closeAllToolbars)
useEventListener(window, MOBILE_PANEL_DRAG_START_EVENT, cancelTourContextMenuForPanelDrag)

// Watch hover state driven by the map and scroll the matching row into view
watch(
  () => workspaceStore.hoveredTourId,
  (id) => {
    if (!id) return
    const el = tourElementRefs.value.get(id)
    if (el) {
      scrollTourRowIntoView(el)
    }
  },
)

const handleTourMouseEnter = (tourId: string) => {
  if (draggedTourId.value || touchDragActive.value) return
  workspaceStore.setActivelyHoveredTourId(tourId)
  workspaceStore.setHoveredTourId(tourId)
}

const handleTourMouseLeave = (tourId: string) => {
  workspaceStore.setActivelyHoveredTourId(null)
  if (workspaceStore.selectedTourId === tourId) {
    workspaceStore.setHoveredTourId(tourId)
    return
  }
  if (workspaceStore.hoveredTourId === tourId) {
    workspaceStore.setHoveredTourId(null)
  }
}

onBeforeUnmount(() => {
  clearLongPressTimer()
  stopAutoScroll()
  workspaceStore.setActivelyHoveredTourId(null)
  if (workspaceStore.hoveredTourId !== null) {
    workspaceStore.setHoveredTourId(null)
  }
})
</script>

<template>
  <div
    v-if="tours.length > 0"
    ref="tourListContainer"
    class="bg-white tour-list-container overflow-x-hidden"
    @dragenter.prevent.stop
    @dragover.prevent.stop
    @dragleave.stop
    @drop.prevent.stop
  >
    <TransitionGroup
      tag="ul"
      class="px-4"
      :name="isPinnedWorkspace ? 'pinned-unpin-slide' : undefined"
    >
      <li
        v-for="(tour, index) in tours"
        :key="tour.clientId"
        :data-tour-id="tour.clientId"
        :class="[
          'group/tour relative -mx-1 overflow-hidden rounded transition-colors duration-200 hover:cursor-pointer',
          dragOverIndex === index
            ? 'border-t-2 border-primary-500'
            : index > 0
              ? 'border-t border-gray-200'
              : '',
          ,
          draggedTourId === tour.clientId ? 'opacity-50' : '',
          touchDraggedTourId === tour.clientId ? 'opacity-50' : '',
        ]"
        :ref="(el) => setTourElementRef(el, tour.clientId)"
        :draggable="!isDragDisabled"
        @dragstart="!isDragDisabled && onDragStart($event, tour.clientId)"
        @dragend="!isDragDisabled && onDragEnd()"
        @dragover.prevent.stop="!isDragDisabled && onDragOver($event, index)"
        @dragleave.stop="!isDragDisabled && onDragLeave($event)"
        @drop.prevent.stop="!isDragDisabled && onDrop($event, index)"
        @dragenter.prevent.stop
        @mouseenter="handleTourMouseEnter(tour.clientId)"
        @mouseleave="handleTourMouseLeave(tour.clientId)"
        @click="
          props.selectionMode
            ? emit('toggle-selection', tour.clientId)
            : handleTourClick(tour.clientId)
        "
        @contextmenu="openTourContextMenu(tour, $event)"
      >
        <div
          class="absolute inset-0 flex items-center justify-between border-y border-amber-200/70 bg-amber-500 px-5 text-white"
          aria-hidden="true"
        >
          <div class="flex items-center gap-3">
            <component :is="tour.isPinned ? PinOffIcon : PinIcon" class="w-5 h-5 shrink-0" />
            <span class="text-xs font-semibold leading-tight">
              {{ tour.isPinned ? 'Unpin' : 'Pin' }}
            </span>
          </div>
          <div class="flex items-center gap-3">
            <span class="text-xs font-semibold leading-tight">
              {{ tour.isPinned ? 'Unpin' : 'Pin' }}
            </span>
            <component :is="tour.isPinned ? PinOffIcon : PinIcon" class="w-5 h-5 shrink-0" />
          </div>
        </div>

        <!-- Main tour row - grid with fixed right-side columns for perfect alignment -->
        <div
          class="relative grid grid-cols-[minmax(0,1fr)_auto] items-start gap-x-0.5 rounded px-1 py-1.5 transition-colors duration-200"
          :class="
            workspaceStore.selectedTourId === tour.clientId ||
            workspaceStore.hoveredTourId === tour.clientId ||
            longPressTourId === tour.clientId
              ? 'bg-gray-200'
              : 'bg-white hover:bg-gray-200'
          "
          :style="{
            transform: `translateX(${tourSwipe.translateX[tour.clientId] ?? 0}px)`,
            transition: tourSwipe.transitioning[tour.clientId] ? 'transform 0.25s ease' : undefined,
          }"
          @touchstart.passive="onTourRowTouchStart($event, tour)"
          @touchmove="tourSwipe.move($event, tour)"
          @touchend="onTourRowTouchEnd(tour)"
          @touchcancel="onTourRowTouchCancel(tour)"
          @transitionend="tourSwipe.clearTransition(tour)"
        >
          <!-- Left content -->
          <div class="flex min-w-0 items-start gap-1.5">
            <BaseCheckbox
              v-if="props.selectionMode"
              class="mr-1 mt-0.5 shrink-0"
              :model-value="props.selectedTourIds?.has(tour.clientId) ?? false"
              @update:model-value="emit('toggle-selection', tour.clientId)"
              @click.stop
            />
            <!-- Type icon -->
            <div
              class="shrink-0 pt-0.5"
              :class="isDragDisabled ? 'cursor-default' : 'cursor-move'"
              @touchstart="!isDragDisabled && onTouchStart($event, tour.clientId)"
            >
              <component
                :is="getTourTypeIcon(tour)"
                class="w-4 h-4"
                :style="{ color: getTourDragHandleColor(tour) }"
              />
            </div>

            <!-- Tour name + metadata container -->
            <div class="flex-1 min-w-0">
              <!-- Inline rename input -->
              <InlineEditField
                v-if="renamingTourId === tour.clientId"
                ref="renameInputRef"
                v-model="renameValue"
                :placeholder="renamePlaceholder"
                @commit="commitInlineRename"
                @cancel="cancelInlineRename"
              />
              <!-- Tour name (click to view details) -->
              <div
                v-else
                class="text-sm cursor-pointer hover:cursor-pointer"
                :class="{
                  'font-medium text-gray-900': workspaceStore.selectedTourId === tour.clientId,
                  'font-medium': workspaceStore.selectedTourId !== tour.clientId,
                  'text-gray-300': !isTourVisible(tour),
                }"
              >
                <!-- Main tour name - truncated on one line -->
                <div class="flex items-center gap-1 min-w-0">
                  <div class="truncate min-w-0" :title="getEffectiveTourName(tour)">
                    {{ getWorkspaceDisplayName(tour) }}
                  </div>
                  <span
                    v-if="getTourCreatorByline(tour)"
                    class="shrink-0 text-xs font-normal text-gray-500"
                    data-testid="tour-creator-byline"
                  >
                    {{ getTourCreatorByline(tour) }}
                  </span>
                  <span
                    v-if="hasCollectionNameOverride(tour) && getTourGlobalName(tour)"
                    class="shrink-0 text-gray-400"
                    :title="getTourGlobalName(tour) ?? undefined"
                    :aria-label="`Global tour name: ${getTourGlobalName(tour)}`"
                  >
                    <TagIcon class="h-3.5 w-3.5" />
                  </span>
                </div>
              </div>

              <!-- Metadata: date, duration, distance, elevation -->
              <div
                v-if="getTourMetaParts(tour).length > 0 && renamingTourId !== tour.clientId"
                class="flex items-center gap-4 mt-0.5 text-xs leading-none"
                :class="isTourVisible(tour) ? 'text-gray-500' : 'text-gray-300'"
              >
                <template v-for="(part, i) in getTourMetaParts(tour)" :key="i">
                  <span>{{ part }}</span>
                </template>
              </div>
            </div>
          </div>

          <!-- Desktop row actions share one right-aligned cell. Visible tours reveal the eye on
               row hover; hidden tours keep EyeOff visible on every screen so they can be restored. -->
          <div class="-mr-1 flex items-center gap-0.5 sm:mr-0">
            <button
              @click.stop="openTourContextMenu(tour, $event)"
              class="hidden h-7 w-7 shrink-0 items-center justify-center rounded text-gray-400 transition-colors hover:cursor-pointer hover:text-gray-700 sm:flex"
              title="Track actions"
              aria-label="Track actions"
            >
              <MoreHorizontalIcon class="w-4 h-4" />
            </button>
            <button
              @click.stop="handleTogglePinned(tour)"
              class="flex h-7 w-7 shrink-0 items-center justify-center rounded transition-colors hover:cursor-pointer"
              :class="[
                tour.isPinned ? 'opacity-100' : 'opacity-0 group-hover/tour:opacity-100',
                tour.isPinned
                  ? 'text-amber-600 hover:text-amber-700'
                  : 'text-gray-300 hover:text-amber-600',
              ]"
              :title="tour.isPinned ? 'Remove from Pinned' : 'Pin'"
              :aria-label="tour.isPinned ? 'Remove from Pinned' : 'Pin'"
            >
              <PinIcon class="w-3.5 h-3.5" :class="{ 'fill-current': tour.isPinned }" />
            </button>
            <button
              @click.stop="toggleTourVisibility(tour.clientId, tour.visible)"
              class="h-7 w-7 shrink-0 items-center justify-center rounded transition-[color,opacity] hover:cursor-pointer"
              :class="
                isTourVisible(tour)
                  ? 'hidden text-gray-500 opacity-0 hover:text-gray-700 group-hover/tour:opacity-100 sm:flex'
                  : 'flex text-gray-500 opacity-100 hover:text-gray-700'
              "
              :title="isTourVisible(tour) ? 'Hide on map' : 'Show on map'"
              :aria-label="isTourVisible(tour) ? 'Hide on map' : 'Show on map'"
            >
              <component :is="isTourVisible(tour) ? EyeIcon : EyeOffIcon" class="h-4 w-4" />
            </button>
          </div>

          <Teleport to="body">
            <div
              v-if="contextMenuTourId === tour.clientId && !isSmallScreen && !mobileContextMenuOpen"
              data-tour-context-menu
              :style="contextMenuStyle"
              class="fixed z-[9999] w-48 bg-white border border-gray-200 rounded-md shadow-lg overflow-hidden py-1"
            >
              <button
                @click.stop="handleContextMenuTogglePinned"
                class="w-full px-3 py-2 text-xs text-left text-gray-700 hover:bg-gray-50 hover:cursor-pointer flex items-center gap-2"
              >
                <PinIcon
                  class="w-3.5 h-3.5 shrink-0"
                  :class="tour.isPinned ? 'fill-current text-amber-600' : 'text-gray-500'"
                />
                {{ tour.isPinned ? 'Unpin' : 'Pin' }}
              </button>
              <button
                v-if="!isTourActivity(tour) && canEditTourContent(tour)"
                @click.stop="handleContextMenuEditRoute"
                class="w-full px-3 py-2 text-xs text-left text-gray-700 hover:bg-gray-50 hover:cursor-pointer flex items-center gap-2"
              >
                <RouteIcon class="w-3.5 h-3.5 text-gray-500 shrink-0" />
                Edit route
              </button>
              <button
                @click.stop="handleContextMenuAddToCollection"
                class="w-full px-3 py-2 text-xs text-left text-gray-700 hover:bg-gray-50 hover:cursor-pointer flex items-center gap-2"
              >
                <SquarePlusIcon class="w-3.5 h-3.5 text-gray-500 shrink-0" />
                Add to collection
              </button>
              <button
                v-if="canEditTourContent(tour)"
                @click.stop="handleContextMenuDuplicateOrEditAsRoute"
                class="w-full px-3 py-2 text-xs text-left text-gray-700 hover:bg-gray-50 hover:cursor-pointer flex items-center gap-2"
              >
                <component
                  :is="isTourActivity(tour) ? RouteIcon : CopyIcon"
                  class="w-3.5 h-3.5 text-gray-500 shrink-0"
                />
                {{ isTourActivity(tour) ? 'Edit as route' : 'Duplicate' }}
              </button>
              <button
                v-if="canEditTourContent(tour)"
                @click.stop="handleContextMenuRename"
                class="w-full px-3 py-2 text-xs text-left text-gray-700 hover:bg-gray-50 hover:cursor-pointer flex items-center gap-2"
              >
                <PenLineIcon class="w-3.5 h-3.5 text-gray-500 shrink-0" />
                Rename
              </button>
              <button
                @click.stop="handleContextMenuDownload"
                class="w-full px-3 py-2 text-xs text-left text-gray-700 hover:bg-gray-50 hover:cursor-pointer flex items-center gap-2"
              >
                <DownloadIcon class="w-3.5 h-3.5 text-gray-500 shrink-0" />
                Download GPX
              </button>
              <hr
                v-if="canRemoveTourFromWorkspace || canDeleteTourGlobally(tour)"
                class="my-1 border-gray-100"
              />
              <button
                v-if="canRemoveTourFromWorkspace"
                @click.stop="handleContextMenuRemoveFromWorkspace"
                class="w-full px-3 py-2 text-xs text-left text-red-600 hover:bg-red-50 hover:cursor-pointer flex items-center gap-2"
              >
                <SquareMinusIcon class="w-3.5 h-3.5 shrink-0" />
                Remove from collection
              </button>
              <button
                v-if="canDeleteTourGlobally(tour)"
                @click.stop="handleContextMenuDeleteTourGlobally"
                class="w-full px-3 py-2 text-xs text-left text-red-600 hover:bg-red-50 hover:cursor-pointer flex items-center gap-2"
              >
                <Trash2Icon class="w-3.5 h-3.5 shrink-0" />
                Delete
              </button>
            </div>
          </Teleport>
        </div>

        <!-- Expanded details section (only shown when expanded) -->
        <div
          v-if="isTourExpanded(tour.clientId)"
          class="mt-2 pl-6 text-xs text-gray-500 border-t border-gray-100 pt-2"
        >
          <!-- Stats in a grid -->
          <div class="grid grid-cols-2 gap-x-4 gap-y-2">
            <div>
              <span>Distance</span>
              <div class="font-medium">{{ formatDistance(tourStats[index].distance) }}</div>
            </div>

            <div v-if="tourStats[index].elevation.gain !== null">
              <span>Elevation</span>
              <div class="font-medium text-green-600">+{{ tourStats[index].elevation.gain }}m</div>
            </div>

            <div v-if="tourStats[index].averageSpeed !== null">
              <span>Avg Speed</span>
              <div class="font-medium text-green-600">
                {{ tourStats[index].averageSpeed.toFixed(1) }} km/h
              </div>
            </div>

            <div v-if="tour.tracks.length > 0">
              <span>Tracks</span>
              <div class="font-medium">{{ tour.tracks.length }}</div>
            </div>

            <div v-if="tour.waypoints.length > 0">
              <span>Waypoints</span>
              <div class="font-medium">{{ tour.waypoints.length }}</div>
            </div>
          </div>

          <!-- Tour description if available -->
          <div v-if="tour.description" class="mt-2 text-xs text-gray-600">
            <p>{{ formatTextWithNewlines(tour.description) }}</p>
          </div>
        </div>
      </li>
    </TransitionGroup>

    <!-- Mobile long-press actions -->
    <MobileActionSheet
      v-if="contextMenuTour"
      :open="mobileContextMenuOpen"
      :title="getWorkspaceDisplayName(contextMenuTour)"
      :title-byline="getTourCreatorByline(contextMenuTour)"
      :dot-color="getTourDragHandleColor(contextMenuTour)"
      @close="closeMobileContextMenu"
      @closed="handleMobileContextMenuClosed"
    >
      <template #header-left>
        <button
          type="button"
          class="flex h-9 w-9 items-center justify-center rounded-full border border-gray-300 bg-white text-gray-600 transition hover:bg-gray-50 hover:text-gray-900"
          title="Close"
          aria-label="Close tour actions"
          @click="closeMobileContextMenu"
        >
          <XIcon class="h-4 w-4" />
        </button>
      </template>

      <WorkspaceTourActionSheetContent
        :tour="contextMenuTour"
        :can-edit="canEditTourContent(contextMenuTour)"
        :can-remove="canRemoveTourFromWorkspace"
        :can-delete="canDeleteTourGlobally(contextMenuTour)"
        :is-activity="isTourActivity(contextMenuTour)"
        :is-visible="isTourVisible(contextMenuTour)"
        :is-downloading="downloadingTourIds.has(contextMenuTour.clientId)"
        @rename="handleContextMenuRename"
        @download="handleContextMenuDownload"
        @add-to-collection="handleContextMenuAddToCollection"
        @toggle-pinned="handleContextMenuTogglePinned"
        @toggle-visibility="handleContextMenuToggleVisibility"
        @edit-route="handleContextMenuEditRoute"
        @duplicate-or-edit-as-route="handleContextMenuDuplicateOrEditAsRoute"
        @remove="handleContextMenuRemoveFromWorkspace"
        @delete="handleContextMenuDeleteTourGlobally"
      />
    </MobileActionSheet>

    <!-- Share tour modal (teleported) -->
    <Teleport to="body">
      <GpxDownloadOptionsModal
        :is-open="isGpxDownloadModalOpen"
        title="Download Tour GPX"
        :is-downloading="isGpxDownloadModalDownloading"
        @close="closeGpxDownloadModal"
        @download="handleDownloadGpx"
      />
      <TourDeleteModal
        :open="isDeleteTourModalOpen"
        :tour="deleteTargetTour"
        :tour-name="deleteTargetTour ? getEffectiveTourName(deleteTargetTour) : null"
        :deleting="isDeletingTour"
        @close="closeDeleteTourModal"
        @confirm="confirmDeleteTourGlobally"
      />
    </Teleport>
  </div>
</template>

<style scoped>
.pinned-unpin-slide-leave-active {
  transition:
    opacity 180ms ease,
    transform 180ms cubic-bezier(0.4, 0, 0.2, 1);
}

.pinned-unpin-slide-move {
  transition: transform 180ms cubic-bezier(0.4, 0, 0.2, 1);
}

.pinned-unpin-slide-leave-to {
  opacity: 0;
  transform: translateY(-14px);
}
</style>
